import { ModalFrame } from "@/components/modal-frame/modal-frame";
import { WebButton } from "@/components/web-button/web-button";
import { useI18n } from "@/utils/i18n";
import { ReactNode, createContext, useCallback, useContext, useState } from "react";
import styles from "./modal-popup.module.css";

type ModalButtonOptions = {
  title: string;
  onClick?: () => void;
};

export type ModalOptions = {
  title: string;
  description?: string | string[];
  error?: unknown;
  okText?: string;
  onOk?: () => void;
  okButton?: ModalButtonOptions;
  cancelButton?: ModalButtonOptions;
};

type ModalContextValue = {
  showModal: (options: ModalOptions) => void;
  hideModal: () => void;
};

const ModalContext = createContext<ModalContextValue | undefined>(undefined);

export function ModalProvider({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const [modal, setModal] = useState<ModalOptions | null>(null);

  const hideModal = useCallback(() => setModal(null), []);
  const closeWithAction = useCallback(
    (action?: () => void) => {
      action?.();
      hideModal();
    },
    [hideModal],
  );

  const okButton = modal?.okButton ?? {
    title: modal?.okText ?? t("common.confirm"),
    onClick: modal?.onOk,
  };
  const descriptionLines = descriptionLinesForModal(modal);

  return (
    <ModalContext.Provider value={{ showModal: setModal, hideModal }}>
      {children}
      {modal && (
        <ModalFrame
          title={modal.title}
          hideCloseIcon
          onClose={hideModal}
          className={styles.modal}
          footer={
            <div className={styles.actions}>
              {modal.cancelButton ? (
                <WebButton
                  className={styles.actionButton}
                  variant="outlined"
                  onClick={() => closeWithAction(modal.cancelButton?.onClick)}
                >
                  {modal.cancelButton.title}
                </WebButton>
              ) : null}
              <WebButton
                className={styles.actionButton}
                onClick={() => closeWithAction(okButton.onClick)}
              >
                {okButton.title}
              </WebButton>
            </div>
          }
        >
          <div className={styles.descriptionList}>
            {descriptionLines.map((line, index) => (
              <p className={styles.descriptionLine} key={`${line}-${index}`}>
                {line}
              </p>
            ))}
          </div>
        </ModalFrame>
      )}
    </ModalContext.Provider>
  );
}

export function useModalProvider() {
  const value = useContext(ModalContext);
  if (!value) {
    throw new Error("useModalProvider must be used within ModalProvider");
  }
  return value;
}

function descriptionLinesForModal(modal: ModalOptions | null): string[] {
  if (!modal) return [];
  if (Array.isArray(modal.description)) return modal.description;
  if (modal.description) return [modal.description];
  if (modal.error instanceof Error) return [modal.error.message];
  return [];
}
